import { useEffect, useState, useRef, useCallback, type Dispatch, type SetStateAction } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { fetchArtistById, checkArtistHasBookingSystem, getArtistBookingSubscriptionStatus, type BookingSubscriptionStatus } from "@/lib/tattooData"
import { QUERY_TATTOOS_ENDPOINT } from "@/config/endpoints"
import type { Artist } from "@/types/models"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { SkeletonGrid } from "@/components/ui/skeleton-grid"
import { MapPin, Pencil, Camera, Copy, Check, Send, ArrowLeft, Sparkles, X, SquarePen, Star, ArrowUpDown } from "lucide-react"
import { RatingSummary } from '@/components/reviews/RatingSummary'
import { useAnalytics } from "@/hooks/useAnalytics"
import { Grid } from "@/components/shared/Grid"
import { useTranslation } from 'react-i18next'
import { useAuthState } from "@/hooks/useAuth"
import { AuthModal } from "@/components/auth/AuthModal"
import { cn } from "@/lib/utils"
import { useUserData } from "@/hooks/useUserData"
import { UploadSheet } from "@/components/artist/UploadSheet"
import {
  StudioWalkthroughArtistUploadTrigger,
  StudioWalkthroughPreviewRow,
} from "@/components/artist/StudioWalkthrough"
import { uploadMultiplePortfolioItems, uploadMultipleFlashes, uploadMultipleHealedPortfolioItems, deletePortfolioItem, deleteFlash, deleteHealedPortfolioItem, updateFlashMetadata, invalidateQueryCache } from "@/lib/portfolioUpload"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { uploadImageToStorageSecure } from "@/lib/secureFileUpload"
import { parsePersistedStudioWalkthroughVideoUrl } from "@/lib/studioWalkthroughUpload"
import { ReviewCard } from "@/components/reviews/ReviewCard"
import { getReviewsByArtistId } from "@/lib/reviewData"
import { BannerImageCropper, type CropData } from "@/components/shared/BannerImageCropper"
import { followArtist, unfollowArtist, isFollowingArtist, getUserFollowingCount } from "@/lib/followData"
import { trackFollowArtist, trackFirstFollowArtist } from "@/lib/metaPixel"

import { ArtistWaitlistRequest } from "@/components/waitlist/ArtistWaitlistRequest"
import { ReviewWriteContent } from "@/components/reviews/ReviewWriteContent"
import { FullScreenDialog, FullScreenDialogContent } from "@/components/ui/full-screen-dialog"
import { FlashDetailContent } from "@/components/details/FlashDetailContent"
import { TattooDetailContent } from "@/components/details/TattooDetailContent"
import { HealedDetailContent } from "@/components/details/HealedDetailContent"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

// Helper function to process escaped newlines in text
const processNewlines = (text: string) => {
  return text.split('\\n').join('\u2028')
}

const ArtistPageSkeleton = () => {
  return (
    <div className={cn(
      'min-h-screen bg-background transition-colors pb-safe',
    )}>
      <div className="relative w-full" style={{ height: 'var(--profile-header-height)' }}>
        <Skeleton className="absolute inset-0 h-full w-full" />
        <div className="phone-container-padded absolute inset-x-0 top-4 z-20 flex justify-between items-start">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div />
        </div>
      </div>

      <div
        className="phone-container-padded flex items-end gap-4 relative z-10"
        style={{ marginTop: 'calc(-1 * var(--profile-avatar-size) / 3)' }}
      >
        <div className="relative flex-shrink-0">
          <Skeleton className="profile-avatar border-4 border-background" />
        </div>
        <div
          className="flex flex-col flex-1 pb-1 gap-2"
        >
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>

      <div
        className="phone-container-padded pt-0"
        style={{ marginTop: 'calc(var(--profile-avatar-size) / 3 + 1rem)' }}
      >
        <div className="mb-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-28" />
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-3">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="h-5 w-48" />
          </div>
        </div>

        <div className="mb-0 flex gap-3 w-full">
          <Skeleton className="flex-1 h-14 rounded-[0.6rem]" />
          <Skeleton className="flex-1 h-14 rounded-[0.6rem]" />
        </div>

        <div className="my-4 flex justify-center">
          <div className="flex gap-8">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>

        <div className="min-h-[200px]">
          <SkeletonGrid itemCount={6} />
        </div>
      </div>
    </div>
  )
}

export default function ArtistPage() {
  const { artistId } = useParams<{ artistId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [artist, setArtist] = useState<Artist | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasBookingSystem, setHasBookingSystem] = useState(false)
  const [, setBookingSubscriptionStatus] = useState<BookingSubscriptionStatus | null>(null)
  const [hasFlashes, setHasFlashes] = useState<boolean | null>(null) // null = loading, true/false = checked
  const [activeTab, setActiveTab] = useState<'onskin' | 'flash' | 'healed' | 'reviews'>('flash')  // Changed from 'onskin' to 'flash'
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [isBioExpanded, setIsBioExpanded] = useState(false)
  const [isEditingBio, setIsEditingBio] = useState(false)
  const [isEditingPortfolio, setIsEditingPortfolio] = useState(false)
  const [editedBio, setEditedBio] = useState("")
  const [editedAddress, setEditedAddress] = useState("")
  const [uploadSheetOpen, setUploadSheetOpen] = useState(false)
  const [uploadType, setUploadType] = useState<'flash' | 'tattoo' | 'healed'>('flash')
  const [refreshKey, setRefreshKey] = useState(0)
  /** Bumps after uploads → full grid reload in useQueryTattoos (avoids soft-replace missing new docs) */
  const [uploadGridRevision, setUploadGridRevision] = useState(0)
  /** Per-tab: hide ids until that grid’s feed refetch drops them (grids must not share one set) */
  const [excludeFlashIdsAfterDelete, setExcludeFlashIdsAfterDelete] = useState<Set<string>>(new Set())
  const [excludePortfolioIdsAfterDelete, setExcludePortfolioIdsAfterDelete] = useState<Set<string>>(new Set())
  const [excludeHealedIdsAfterDelete, setExcludeHealedIdsAfterDelete] = useState<Set<string>>(new Set())

  const reconcileExcluded = useCallback((itemIds: string[], setExcluded: Dispatch<SetStateAction<Set<string>>>) => {
    const idSet = new Set(itemIds)
    setExcluded((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of prev) {
        if (!idSet.has(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  const onFlashFeedIdsChange = useCallback(
    (itemIds: string[]) => reconcileExcluded(itemIds, setExcludeFlashIdsAfterDelete),
    [reconcileExcluded]
  )
  const onPortfolioFeedIdsChange = useCallback(
    (itemIds: string[]) => reconcileExcluded(itemIds, setExcludePortfolioIdsAfterDelete),
    [reconcileExcluded]
  )
  const onHealedFeedIdsChange = useCallback(
    (itemIds: string[]) => reconcileExcluded(itemIds, setExcludeHealedIdsAfterDelete),
    [reconcileExcluded]
  )
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null)
  const [showBannerCropper, setShowBannerCropper] = useState(false)
  const [addressCopied, setAddressCopied] = useState(false)
  const [isAddressExpanded, setIsAddressExpanded] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)
  const [isFollowLoading, setIsFollowLoading] = useState(false)
  const [showUploadMenu, setShowUploadMenu] = useState(false)
  const uploadMenuRef = useRef<HTMLDivElement>(null)
  const [flashMetadataChanges, setFlashMetadataChanges] = useState<Map<string, { minPrice?: string; minSize?: string; booked?: boolean }>>(new Map())
  const [hasHealed, setHasHealed] = useState<boolean | null>(null) // null = loading, true/false = checked
  const [hasOnskin, setHasOnskin] = useState<boolean | null>(null) // null = loading, true/false = checked
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showReviewAuthModal, setShowReviewAuthModal] = useState(false)
  const [reviews, setReviews] = useState<any[]>([])
  const [loadingReviews, setLoadingReviews] = useState(true)
  const [refreshReviewsKey, setRefreshReviewsKey] = useState(0)
  const [reviewSortOrder, setReviewSortOrder] = useState<'latest' | 'oldest'>('latest')
  const [isReviewSortOpen, setIsReviewSortOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [detailType, setDetailType] = useState<'flash' | 'tattoo' | 'healed'>('flash')
  const [detailId, setDetailId] = useState<string>('')
  const BIO_CHARACTER_LIMIT = 150
  const matcherSessionId = searchParams.get('matcherSessionId')
  const matcherUserId = searchParams.get('matcherUserId')
  const matcherSelectedCity = searchParams.get('matcherSelectedCity') || ''
  const matcherIntentMode = searchParams.get('matcherIntentMode') === 'specific' ? 'specific' : 'exploring'
  const matcherIdeaText = searchParams.get('matcherIdeaText') || ''
  const sourceParam = searchParams.get('source') || 'artist_profile'
  
  // Waitlist mode - activated by URL param
  const isWaitlistMode = searchParams.get('waitlistMode') === 'true'

  // Helper function to determine if bio should be truncated
  const shouldTruncateBio = (bio: string) => {
    return bio.length > BIO_CHARACTER_LIMIT
  }
  const { trackEvent, trackAuthPrompt } = useAnalytics()
  const { user } = useAuthState()
  const { isOwnArtistPage, userData, isAdminModeActive } = useUserData()
  const { t } = useTranslation()
  // Check if viewing own artist page
  const isOwnPage = isOwnArtistPage(artistId)
  
  // Check admin status - only show admin features if admin mode is enabled
  const canEdit = isOwnPage || isAdminModeActive
  
  console.log('[ArtistPage] Edit permissions:', { 
    pageArtistId: artistId, 
    userArtistId: userData?.artistId,
    userEmail: user?.email,
    isOwnPage, 
    isAdminModeActive,
    canEdit 
  })

  const avatarOverlapDivisor = hasBookingSystem ? 4 : 3

  useEffect(() => {
    if (!artistId || artistId === 'undefined') {
      navigate('/feed', { replace: true })
      return
    }

    setLoading(true)
    fetchArtistById(artistId)
      .then((artist) => {
        setArtist(artist)
        // Track artist page view
        trackEvent('artist_viewed', {
          artist_id: artistId,
          source: sourceParam
        })
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [artistId, sourceParam, trackEvent, navigate])

  // Check if artist has booking system and get subscription status
  useEffect(() => {
    if (!artistId) {
      setHasBookingSystem(false)
      setBookingSubscriptionStatus(null)
      return
    }

    const checkBookingSystem = async () => {
      try {
        const [hasBooking, status] = await Promise.all([
          checkArtistHasBookingSystem(artistId),
          getArtistBookingSubscriptionStatus(artistId)
        ])
        setHasBookingSystem(hasBooking)
        setBookingSubscriptionStatus(status)
      } catch (error) {
        console.error('[ArtistPage] Error checking booking system:', error)
        setHasBookingSystem(false)
        setBookingSubscriptionStatus(null)
      }
    }

    checkBookingSystem()
  }, [artistId])

  // Check if artist has flashes using backend API (matches Grid's data source)
  useEffect(() => {
    if (!artistId) {
      setHasFlashes(false)
      return
    }

    const checkFlashes = async () => {
      try {
        // Use backend API to check if artist has flashes (same as Grid)
        const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            collections: ['flashes'],
            sorting: 'recent',
            saved: false,
            userId: user?.uid || null,
            cities: undefined,
            artistId: artistId,
            style: undefined,
            includeBooked: false,
            deals: false,
            isAdmin: isAdminModeActive
          })
        })
        
        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}`)
        }
        
        const data = await response.json()
        const artistHasFlashes = (data.left && data.left.length > 0) || (data.right && data.right.length > 0)
        setHasFlashes(artistHasFlashes)
      } catch (error) {
        console.error('[ArtistPage] Error checking flashes:', error)
        setHasFlashes(false)
      }
    }

    checkFlashes()
  }, [artistId, refreshKey, uploadGridRevision, user, isAdminModeActive])

  // Set default tab based on what collections the artist has
  // Only set default on initial load, don't override user selections
  const hasSetDefaultTabRef = useRef(false)
  
  useEffect(() => {
    // Only set default tab once on initial load
    if (hasSetDefaultTabRef.current) {
      return
    }
    
    // Only update when we have all the data loaded
    if (hasFlashes === null || hasOnskin === null || hasHealed === null) {
      return
    }

    // Mark that we've set the default tab
    hasSetDefaultTabRef.current = true

    // Try to set the tab to the first available collection
    if (hasFlashes) {
      setActiveTab('flash')
    } else if (hasOnskin) {
      setActiveTab('onskin')
    } else if (hasHealed) {
      setActiveTab('healed')
    }
  }, [hasFlashes, hasOnskin, hasHealed])

  // If current active tab becomes empty (e.g., all items deleted), switch to a tab with content
  useEffect(() => {
    // Skip if we're still loading the checks
    if (hasFlashes === null || hasOnskin === null || hasHealed === null) {
      return
    }

    // Check if current active tab is now empty and switch to one with content
    if (activeTab === 'flash' && !hasFlashes) {
      if (hasOnskin) {
        setActiveTab('onskin')
      } else if (hasHealed) {
        setActiveTab('healed')
      } else {
        setActiveTab('reviews')
      }
    } else if (activeTab === 'onskin' && !hasOnskin) {
      if (hasFlashes) {
        setActiveTab('flash')
      } else if (hasHealed) {
        setActiveTab('healed')
      } else {
        setActiveTab('reviews')
      }
    } else if (activeTab === 'healed' && !hasHealed) {
      if (hasFlashes) {
        setActiveTab('flash')
      } else if (hasOnskin) {
        setActiveTab('onskin')
      } else {
        setActiveTab('reviews')
      }
    }
  }, [activeTab, hasFlashes, hasOnskin, hasHealed])

  // Check if artist has healed portfolio items using backend API (matches Grid's data source)
  useEffect(() => {
    if (!artistId) {
      setHasHealed(false)
      return
    }

    const checkHealed = async () => {
      try {
        // Use backend API to check if artist has healed items (same as Grid)
        const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            collections: ['healedPortfolioItems'],
            sorting: 'recent',
            saved: false,
            userId: user?.uid || null,
            cities: undefined,
            artistId: artistId,
            style: undefined,
            includeBooked: false,
            deals: false,
            isAdmin: isAdminModeActive
          })
        })
        
        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}`)
        }
        
        const data = await response.json()
        const artistHasHealed = (data.left && data.left.length > 0) || (data.right && data.right.length > 0)
        setHasHealed(artistHasHealed)
      } catch (error) {
        console.error('[ArtistPage] Error checking healed items:', error)
        setHasHealed(false)
      }
    }

    checkHealed()
  }, [artistId, refreshKey, uploadGridRevision, user, isAdminModeActive])

  // Check if artist has onskin portfolio items using backend API (matches Grid's data source)
  useEffect(() => {
    if (!artistId) {
      setHasOnskin(false)
      return
    }

    const checkOnskin = async () => {
      try {
        // Use backend API to check if artist has onskin items (same as Grid)
        const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            collections: ['portfolioItems'],
            sorting: 'recent',
            saved: false,
            userId: user?.uid || null,
            cities: undefined,
            artistId: artistId,
            style: undefined,
            includeBooked: false,
            deals: false,
            isAdmin: isAdminModeActive
          })
        })
        
        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}`)
        }
        
        const data = await response.json()
        const artistHasOnskin = (data.left && data.left.length > 0) || (data.right && data.right.length > 0)
        setHasOnskin(artistHasOnskin)
      } catch (error) {
        console.error('[ArtistPage] Error checking onskin items:', error)
        setHasOnskin(false)
      }
    }

    checkOnskin()
  }, [artistId, refreshKey, uploadGridRevision, user, isAdminModeActive])

  // Fetch reviews for this artist
  useEffect(() => {
    if (!artistId) {
      setReviews([])
      setLoadingReviews(false)
      return
    }

    const fetchReviews = async () => {
      try {
        setLoadingReviews(true)
        const reviewsData = await getReviewsByArtistId(artistId)
        setReviews(reviewsData)
      } catch (error) {
        console.error('[ArtistPage] Error fetching reviews:', error)
        setReviews([])
      } finally {
        setLoadingReviews(false)
      }
    }

    fetchReviews()
  }, [artistId, refreshReviewsKey])
  useEffect(() => {
    const checkFollowingStatus = async () => {
      if (!user || !artistId) {
        setIsFollowing(false)
        return
      }

      try {
        const followingStatus = await isFollowingArtist(artistId, user.uid)
        setIsFollowing(followingStatus)
      } catch (error) {
        console.error('[ArtistPage] Error checking following status:', error)
        setIsFollowing(false)
      }
    }

    checkFollowingStatus()
  }, [user, artistId])

  // Close upload menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(event.target as Node)) {
        setShowUploadMenu(false)
      }
    }

    if (showUploadMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showUploadMenu])

  if (loading) {
    return (
      <ArtistPageSkeleton />
    )
  }
  if (!artist) return <div className="min-h-screen bg-background pb-safe phone-container-padded py-8">Artist not found</div>
  if (!artistId) return <div className="min-h-screen bg-background pb-safe phone-container-padded py-8">Invalid artist ID</div>

  const handleTabChange = (tab: 'flash' | 'onskin' | 'healed') => {
    if (tab === activeTab) return
    setActiveTab(tab)
    trackEvent('artist_tab_clicked', {
      tab_key: tab,
      page: 'artist_page',
      artist_id: artistId
    })
  }

  const handleContactArtist = async () => {
    if (!artist) return

    const chatParams = new URLSearchParams({
      artistId: artist.id,
      source: sourceParam,
    })

    if (matcherSessionId) {
      chatParams.set('matcherSessionId', matcherSessionId)
      chatParams.set('matcherUserId', matcherUserId || '')
      chatParams.set('matcherSelectedCity', matcherSelectedCity)
      chatParams.set('matcherIntentMode', matcherIntentMode)

      if (matcherIdeaText.trim()) {
        chatParams.set('matcherIdeaText', matcherIdeaText)
      }
    }

    trackEvent('artist_message_clicked', {
      artist_id: artist.id,
      source: sourceParam
    })
    navigate(`/chats/new?${chatParams.toString()}`)
  }

  const handleBackButtonClick = () => {
    if (matcherSessionId) {
      const matcherParams = new URLSearchParams({
        resume: 'results',
        sessionId: matcherSessionId,
        userId: matcherUserId || '',
        selectedCity: matcherSelectedCity,
        intentMode: matcherIntentMode,
      })

      if (matcherIdeaText.trim()) {
        matcherParams.set('ideaText', matcherIdeaText)
      }

      navigate(`/tattoo-artist-matcher?${matcherParams.toString()}`)
      return
    }

    // Try to go back in navigation history, but if there's nowhere to go back to
    // (e.g., deep link navigation), default to the artists page
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/artists')
    }
  }

  const handleFollowClick = async () => {
    if (!user) {
      // If user is not authenticated, show login modal
      await trackAuthPrompt('artist_page_follow')
      setShowLoginModal(true)
      return
    }

    if (!artistId) return

    try {
      setIsFollowLoading(true)
      
      if (isFollowing) {
        // Unfollow the artist
        await unfollowArtist(artistId, user.uid)
        setIsFollowing(false)
        trackEvent('artist_follow_clicked', {
          artist_id: artistId,
          is_following: false,
          source: 'artist_profile'
        })
        console.log('[ArtistPage] Unfollowed artist:', artistId)
      } else {
        // Follow the artist
        // Check if this is the user's first follow before following
        const followingCountBefore = await getUserFollowingCount(user.uid)
        const isFirstFollow = followingCountBefore === 0
        
        await followArtist(artistId, user.uid)
        setIsFollowing(true)
        trackEvent('artist_follow_clicked', {
          artist_id: artistId,
          is_following: true,
          source: 'artist_profile'
        })
        console.log('[ArtistPage] Followed artist:', artistId)
        // Track artist follow event
        try {
          if (isFirstFollow) {
            trackFirstFollowArtist(artistId)
          } else {
            trackFollowArtist(artistId)
          }
        } catch (error) {
          console.error('[ArtistPage] Failed to track artist follow:', error)
        }
      }
    } catch (error) {
      console.error('[ArtistPage] Error toggling follow:', error)
      alert('Failed to update follow status. Please try again.')
    } finally {
      setIsFollowLoading(false)
    }
  }

  const handleLoginSuccess = async () => {
    setShowLoginModal(false)
    // User can now manually choose to contact the artist if they want
  }

  const handleWriteReviewClick = async () => {
    if (!user) {
      await trackAuthPrompt('artist_page_write_review')
      setShowReviewAuthModal(true)
      return
    }
    setShowReviewModal(true)
  }

  const handleReviewAuthSuccess = () => {
    setShowReviewAuthModal(false)
    setShowReviewModal(true)
  }

  const handleCopyAddress = async () => {
    const addressText = artist?.location.address || `${artist?.location.city}, ${artist?.location.country}`
    
    try {
      await navigator.clipboard.writeText(addressText)
      setAddressCopied(true)
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setAddressCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy address:', error)
    }
  }

  const cityCountry = `${artist?.location?.city || ''}${artist?.location?.city && artist?.location?.country ? ', ' : ''}${artist?.location?.country || ''}`
  const fullAddressSingleLine = (artist?.location?.address || '')
    .replace(/\s*\n+\s*/g, ', ')
    .replace(/\s{2,}/g, ' ')
    .trim()
  const collapsedAddress = cityCountry || fullAddressSingleLine

  const handleEditClick = () => {
    if (!isEditingBio && !isEditingPortfolio) {
      // Enter edit mode for both bio and portfolio
      setIsEditingBio(true)
      setIsEditingPortfolio(true)
      setEditedBio(artist?.bio || "")
      setEditedAddress(artist?.location?.address || `${artist?.location?.city || ''}${artist?.location?.city && artist?.location?.country ? ', ' : ''}${artist?.location?.country || ''}`)
    }
  }

  const handleSaveBio = async () => {
    if (!artistId) return
    
    try {
      console.log('Saving bio:', editedBio)
      
      // Update Firestore
      await updateDoc(doc(db, 'artists', artistId), {
        bio: editedBio,
        'location.address': editedAddress
      })
      
      console.log('Bio updated successfully')
      
      // Update local artist state
      if (artist) {
        setArtist({ 
          ...artist, 
          bio: editedBio,
          location: { 
            ...artist.location, 
            address: editedAddress 
          }
        })
      }
      
      // Also save any pending flash metadata changes
      if (flashMetadataChanges.size > 0) {
        console.log('Saving pending flash metadata changes...')
        await handleSaveAllFlashMetadata()
      }
      
      setIsEditingBio(false)
      setIsEditingPortfolio(false)
    } catch (error) {
      console.error('Bio update failed:', error)
      alert(`Bio update failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }


  const handleUpload = async (files: File[], metadata?: Array<{ minPrice: string; minSize: string }>) => {
    if (!artistId) {
      const errorMsg = 'No artist ID available. Please refresh the page and try again.'
      console.error('[Upload Error]', errorMsg)
      throw new Error(errorMsg)
    }

    try {
      if (uploadType === 'flash') {
        if (!metadata || metadata.length !== files.length) {
          throw new Error('Metadata is required for all flashes')
        }
        console.log(`[Upload] Starting upload of ${files.length} flash(es)`)
        
        // Upload each flash with its own metadata
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const meta = metadata[i]
          console.log(`[Upload] Uploading flash ${i + 1}/${files.length}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) with price: ${meta.minPrice}€, size: ${meta.minSize}cm`)
          
          try {
            await uploadMultipleFlashes([file], artistId, meta.minPrice, meta.minSize)
            console.log(`[Upload] ✅ Flash ${i + 1}/${files.length} uploaded successfully`)
          } catch (fileError) {
            console.error(`[Upload] ❌ Failed to upload flash ${i + 1}/${files.length}:`, fileError)
            throw new Error(`Failed to upload "${file.name}": ${fileError instanceof Error ? fileError.message : 'Unknown error'}. Please try again or contact support if the problem persists.`)
          }
        }
        console.log('[Upload] ✅ All flashes uploaded successfully!')
      } else if (uploadType === 'healed') {
        console.log(`[Upload] Starting upload of ${files.length} healed portfolio item(s)`)
        
        // Upload each healed item individually
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          console.log(`[Upload] Uploading healed item ${i + 1}/${files.length}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`)
          
          try {
            await uploadMultipleHealedPortfolioItems([file], artistId)
            console.log(`[Upload] ✅ Healed item ${i + 1}/${files.length} uploaded successfully`)
          } catch (fileError) {
            console.error(`[Upload] ❌ Failed to upload healed item ${i + 1}/${files.length}:`, fileError)
            throw new Error(`Failed to upload "${file.name}": ${fileError instanceof Error ? fileError.message : 'Unknown error'}. Please try again or contact support if the problem persists.`)
          }
        }
        console.log('[Upload] ✅ All healed portfolio items uploaded successfully!')
      } else {
        console.log(`[Upload] Starting upload of ${files.length} portfolio item(s)`)
        
        // Upload each tattoo individually (same as flashes)
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          console.log(`[Upload] Uploading tattoo ${i + 1}/${files.length}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`)
          
          try {
            await uploadMultiplePortfolioItems([file], artistId)
            console.log(`[Upload] ✅ Tattoo ${i + 1}/${files.length} uploaded successfully`)
          } catch (fileError) {
            console.error(`[Upload] ❌ Failed to upload tattoo ${i + 1}/${files.length}:`, fileError)
            throw new Error(`Failed to upload "${file.name}": ${fileError instanceof Error ? fileError.message : 'Unknown error'}. Please try again or contact support if the problem persists.`)
          }
        }
        console.log('[Upload] ✅ All portfolio items uploaded successfully!')
      }
      
      // Invalidate again after all files (per-file upload also invalidates; this guarantees refetch sees fresh cache)
      try {
        await invalidateQueryCache()
      } catch (e) {
        console.warn('[Upload] Cache invalidation:', e)
      }
      setUploadGridRevision((u) => u + 1)
    } catch (error) {
      console.error('[Upload] ❌ Upload failed:', error)
      // Re-throw to let UploadSheet display the error
      throw error
    }
  }

  const handleDeletePortfolioItem = async (itemId: string) => {
    try {
      console.log('Deleting portfolio item:', itemId)
      await deletePortfolioItem(itemId)
      console.log('Portfolio item deleted successfully')
      setExcludePortfolioIdsAfterDelete((prev) => new Set(prev).add(itemId))
      setRefreshKey((prev) => prev + 1)
    } catch (error) {
      console.error('Delete failed:', error)
      alert(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDeleteFlash = async (flashId: string) => {
    try {
      console.log('Deleting flash:', flashId)
      await deleteFlash(flashId)
      console.log('Flash deleted successfully')
      setExcludeFlashIdsAfterDelete((prev) => new Set(prev).add(flashId))
      setRefreshKey((prev) => prev + 1)
    } catch (error) {
      console.error('Delete failed:', error)
      alert(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDeleteHealedPortfolioItem = async (itemId: string) => {
    try {
      console.log('Deleting healed portfolio item:', itemId)
      await deleteHealedPortfolioItem(itemId)
      console.log('Healed portfolio item deleted successfully')
      setExcludeHealedIdsAfterDelete((prev) => new Set(prev).add(itemId))
      setRefreshKey((prev) => prev + 1)
    } catch (error) {
      console.error('Delete failed:', error)
      alert(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleFlashMetadataChange = (itemId: string, updates: { minPrice?: string; minSize?: string; booked?: boolean }) => {
    console.log('[ArtistPage] handleFlashMetadataChange called:', { itemId, updates })
    setFlashMetadataChanges(prev => {
      const newChanges = new Map(prev)
      const existingChanges = newChanges.get(itemId) || {}
      const merged = { ...existingChanges, ...updates }
      newChanges.set(itemId, merged)
      console.log('[ArtistPage] Updated pending changes for flash:', itemId, merged)
      console.log('[ArtistPage] Total pending changes:', newChanges.size)
      return newChanges
    })
  }

  const handleRefreshFlashes = () => {
    console.log('[ArtistPage] Refreshing flashes after discount change')
    setRefreshKey(prev => prev + 1)
  }

  const handleFlashMetadataSave = async (itemId: string, directUpdates?: { minPrice?: string; minSize?: string; booked?: boolean }) => {
    console.log('[ArtistPage] handleFlashMetadataSave called for:', itemId, { directUpdates })
    
    // If direct updates are provided (from individual save), use those
    // Otherwise, check for pending changes (from batch save)
    const changes = directUpdates || flashMetadataChanges.get(itemId)
    
    if (!changes) {
      console.log('[ArtistPage] No changes to save for:', itemId)
      return
    }

    console.log('[ArtistPage] Saving changes to Firestore:', { itemId, changes })
    
    try {
      await updateFlashMetadata(itemId, changes)
      console.log('[ArtistPage] Flash metadata updated successfully in Firestore:', itemId)
      
      // Always remove from pending changes after a successful save
      setFlashMetadataChanges(prev => {
        const newChanges = new Map(prev)
        newChanges.delete(itemId)
        console.log('[ArtistPage] Removed from pending changes. Remaining:', newChanges.size)
        return newChanges
      })
      
      // Don't refresh immediately - let the user continue editing other items
      // Refresh will happen when they click the global Save button or exit edit mode
    } catch (error) {
      console.error('[ArtistPage] Failed to update flash metadata:', error)
      alert(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      throw error
    }
  }

  const handleSaveAllFlashMetadata = async () => {
    if (flashMetadataChanges.size === 0) {
      console.log('No pending flash metadata changes to save')
      return
    }

    console.log(`Saving ${flashMetadataChanges.size} pending flash metadata changes...`)
    const updatePromises = Array.from(flashMetadataChanges.entries()).map(
      ([itemId, changes]) => updateFlashMetadata(itemId, changes)
    )

    try {
      await Promise.all(updatePromises)
      console.log('All flash metadata updated successfully')
      setFlashMetadataChanges(new Map())
      // Now refresh to show the updated values from Firestore
      setRefreshKey(prev => prev + 1)
    } catch (error) {
      console.error('Failed to update some flash metadata:', error)
      alert(`Some updates failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleOpenDetail = (itemId: string, type: 'flash' | 'tattoo' | 'healed') => {
    setDetailId(itemId)
    setDetailType(type)
    setDetailModalOpen(true)
  }

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false)
    setDetailId('')
  }

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !artistId || !user?.uid) return

    try {
      setUploadingAvatar(true)
      
      // Upload using the same secure method as onboarding
      // Use artistId for the storage path, but current user's ID for tracking metadata
      const downloadURL = await uploadImageToStorageSecure(
        file,
        `artist-avatars/${artistId}`,
        user.uid
      )
      
      // Update Firestore
      await updateDoc(doc(db, 'artists', artistId), {
        avatarUrl: downloadURL
      })
      
      // Update local state
      if (artist) {
        setArtist({ ...artist, avatarUrl: downloadURL })
      }
      
      console.log('Avatar updated successfully')
    } catch (error) {
      console.error('Avatar upload failed:', error)
      alert(`Avatar upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleBannerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setBannerImageFile(file)
    setShowBannerCropper(true)
  }

  const handleBannerCropComplete = async (croppedImageUrl: string, _cropData: CropData) => {
    if (!artistId || !user?.uid) return

    setShowBannerCropper(false)
    setUploadingBanner(true)

    try {
      // Convert the cropped image URL to a File for upload
      const response = await fetch(croppedImageUrl)
      const blob = await response.blob()
      const croppedFile = new File([blob], `banner-${Date.now()}.jpg`, { type: 'image/jpeg' })

      // Upload using the same secure method as onboarding
      // Use artistId for the storage path, but current user's ID for tracking metadata
      const downloadURL = await uploadImageToStorageSecure(
        croppedFile,
        `artist-banners/${artistId}`,
        user.uid
      )
      
      // Update Firestore
      await updateDoc(doc(db, 'artists', artistId), {
        backdropUrl: downloadURL
      })
      
      // Update local state
      if (artist) {
        setArtist({ ...artist, backdropUrl: downloadURL })
      }
      
      console.log('Banner updated successfully')
    } catch (error) {
      console.error('Banner upload failed:', error)
      alert(`Banner upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploadingBanner(false)
      setBannerImageFile(null)
    }
  }

  const handleBannerCropCancel = () => {
    setShowBannerCropper(false)
    setBannerImageFile(null)
  }

  const handleExitWaitlistMode = () => {
    setSearchParams({})
  }

  const studioWalkthroughPlayUrl = parsePersistedStudioWalkthroughVideoUrl(artist.studioWalkthroughVideoUrl)

  // Sort reviews based on selected order
  const getSortedReviews = () => {
    const reviewsCopy = [...reviews]
    switch (reviewSortOrder) {
      case 'latest':
        return reviewsCopy.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      case 'oldest':
        return reviewsCopy.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      default:
        return reviewsCopy
    }
  }

  const reviewSortOptions: { value: 'latest' | 'oldest'; label: string }[] = [
    { value: 'latest', label: 'Most Recent' },
    { value: 'oldest', label: 'Earliest' },
  ]

  const selectedReviewSortLabel = reviewSortOptions.find((option) => option.value === reviewSortOrder)?.label || 'Most Recent'

  return (
    <>
      <div className={cn(
        'min-h-screen bg-background transition-colors pb-safe',
      )}>
        {/* Header: solid band clears notch/island — hero + controls sit below */}
        <div className="relative flex w-full flex-col">
          <div
            aria-hidden
            className="pointer-events-none w-full shrink-0 bg-background min-h-0 h-safe-native-top"
          />
          <div className="relative w-full" style={{ height: 'var(--profile-header-height)' }}>
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-gradient-to-t to-primary from-secondary bg-cover bg-center phone-container-banner"
              style={{
                backgroundImage: artist.backdropUrl ? `url(${artist.backdropUrl})` : undefined,
                backgroundBlendMode: artist.backdropUrl ? 'overlay' : 'normal',
                zIndex: 1,
              }}
            />
          
            {/* Back/Close Button - Top Corner (respects page bounds) */}
            <div className="phone-container-padded absolute inset-x-0 top-4 z-20 flex justify-between items-start">
            {!isWaitlistMode ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackButtonClick}
                className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </Button>
            ) : (
              <div />
            )}
            
            {isWaitlistMode && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExitWaitlistMode}
                className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border"
              >
                <X className="h-5 w-5 text-foreground" />
              </Button>
            )}

          </div>
          
          {/* Edit Banner Overlay - Show in edit mode */}
          {canEdit && isEditingBio && (
            <>
              <input
                type="file"
                id="banner-upload"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleBannerChange}
                disabled={uploadingBanner}
              />
              <label
                htmlFor="banner-upload"
                className="phone-container-banner absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/40 cursor-pointer animate-in fade-in duration-200"
              >
                <Camera className="w-8 h-8 text-white" />
                <span className="text-sm font-medium text-white">
                  {uploadingBanner ? 'Uploading...' : 'Change'}
                </span>
              </label>
            </>
          )}
          </div>
        </div>

        {/* Avatar, Name, and Display Name (left-aligned, overlapping header) */}
        <div
          className="phone-container-padded flex items-end gap-4 relative z-10"
          style={{ marginTop: `calc(-1 * var(--profile-avatar-size) / ${avatarOverlapDivisor})` }}
        >
          {artist.avatarUrl && (
            <div className="relative flex-shrink-0">
              <img
                src={artist.avatarUrl}
                alt={artist.displayName}
                className={`profile-avatar object-cover shadow-lg border-4 ${
                  hasBookingSystem ? 'border-amber-400' : 'border-background'
                }`}
                style={{ 
                  zIndex: 10,
                  width: 'var(--profile-avatar-size)',
                  height: 'var(--profile-avatar-size)'
                }}
              />
              
              {/* Edit Avatar Overlay - Show in edit mode */}
              {canEdit && isEditingBio && (
                <>
                  <input
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarChange}
                    disabled={uploadingAvatar}
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-1 cursor-pointer animate-in fade-in duration-200 rounded-[var(--profile-avatar-radius)]"
                    style={{ 
                      zIndex: 11,
                      background: 'radial-gradient(circle, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
                    }}
                  >
                    <Camera className="w-6 h-6 text-white" />
                    <span className="text-xs font-medium text-white">
                      {uploadingAvatar ? 'Uploading...' : 'Change'}
                    </span>
                  </label>
                </>
              )}
            </div>
          )}
          <div 
            className="flex flex-col flex-1 min-w-0 justify-evenly py-1"
            style={{ height: 'calc(var(--profile-avatar-size) * 2 / 3)' }}
          >
            {artist.displayName && (
              <h1
                className="text-profile-title text-foreground leading-tight mt-2 truncate"
                title={artist.displayName}
              >
                {artist.displayName}
              </h1>
            )}
            {artistId && (
              <button
                type="button"
                onClick={() => setActiveTab('reviews')}
                aria-label="Open reviews"
                className="p-0 bg-transparent border-0 text-left inline-flex"
              >
                <RatingSummary artistId={artistId} className="cursor-pointer" />
              </button>
            )}
          </div>

        </div>
        
        {/* Content Section */}
        {isWaitlistMode ? (
          <div
            className="phone-container-padded pt-0 pb-20"
          >
            <ArtistWaitlistRequest 
              artistDisplayName={artist.displayName}
              onSuccess={() => {
                // Optionally handle success
              }}
            />
          </div>
        ) : (
          <div
            className="phone-container-padded pt-0 pb-20"
          >
            {/* Description - Editable when in edit mode */}
          {isEditingBio ? (
            <div className="mb-2 mt-1">
              <textarea
                value={editedBio}
                onChange={(e) => setEditedBio(e.target.value)}
                className="w-full min-h-[120px] p-3 text-base border-2 border-primary rounded-lg focus:outline-none focus:ring-0 resize-none animate-in fade-in duration-200"
                placeholder="Write your bio..."
              />
            </div>
          ) : artist.bio && (
            <div className="mb-2 mt-1">
              <div className="relative">
                <p 
                className={`text-foreground text-base text-profile-bio whitespace-pre-wrap ${
                  shouldTruncateBio(artist.bio) && !isBioExpanded ? 'max-h-[5.5em] overflow-hidden' : ''
                }`}
              >
                {processNewlines(artist.bio)}
              </p>
              {shouldTruncateBio(artist.bio) && !isBioExpanded && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-[2.5em] bg-gradient-to-b from-transparent to-background"
                  aria-hidden="true"
                />
              )}
            </div>
            {shouldTruncateBio(artist.bio) && (
              <button
                onClick={async () => {
                  const newExpandedState = !isBioExpanded;
                  setIsBioExpanded(newExpandedState);
                }}
                className="mt-2 text-primary hover:text-primary/80 text-base font-medium flex items-center gap-1"
              >
                  
                  {isBioExpanded ? (
                    <>
                      Show less
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="transform rotate-180"
                      >
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </>
                  ) : (
                    <>
                      Show more
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Location with Map Icon (editable in edit mode) */}
          <div className="mb-3">
            {isEditingBio ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 flex-shrink-0" />
                <input
                  type="text"
                  value={editedAddress}
                  onChange={(e) => setEditedAddress(e.target.value)}
                  className="flex-1 text-base p-2 border-2 border-primary rounded-lg focus:outline-none focus:ring-0"
                  placeholder="Address"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground group">
                <button
                  type="button"
                  onClick={() => {
                    if (fullAddressSingleLine) {
                      setIsAddressExpanded((prev) => !prev)
                    }
                  }}
                  aria-expanded={isAddressExpanded}
                  className={`flex items-center gap-2 text-left transition-colors ${fullAddressSingleLine ? 'hover:text-foreground cursor-pointer' : 'cursor-default'}`}
                >
                  <MapPin className="w-5 h-5 flex-shrink-0" />
                  <span className={`text-base text-left ${isAddressExpanded && fullAddressSingleLine ? 'whitespace-normal break-words' : 'whitespace-nowrap'}`}>
                    {isAddressExpanded && fullAddressSingleLine ? fullAddressSingleLine : collapsedAddress}
                  </span>
                </button>
                {isAddressExpanded && fullAddressSingleLine && (
                  <button
                    type="button"
                    onClick={handleCopyAddress}
                    className="flex items-center"
                    aria-label="Copy address"
                  >
                    {addressCopied ? (
                      <Check className="w-4 h-4 flex-shrink-0 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 flex-shrink-0 text-foreground opacity-70 hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {artistId &&
            (canEdit ? (
              <div className="mb-3">
                <StudioWalkthroughArtistUploadTrigger
                  artistId={artistId}
                  userUid={user?.uid}
                  currentVideoUrl={artist.studioWalkthroughVideoUrl}
                  onVideoUrlUpdated={(url) => {
                    setArtist((previous) =>
                      previous ? { ...previous, studioWalkthroughVideoUrl: url } : previous
                    )
                  }}
                />
              </div>
            ) : (
              studioWalkthroughPlayUrl && (
                <div className="mb-3">
                  <StudioWalkthroughPreviewRow videoUrl={studioWalkthroughPlayUrl} />
                </div>
              )
            ))}

          {/* Edit and Add Designs Buttons - show if viewing own page or admin */}
          {canEdit && (
            <div className="mb-0 flex gap-3 w-full">
              <Button 
                onClick={isEditingBio ? () => {
                  setIsEditingBio(false);
                  setIsEditingPortfolio(false);
                } : handleEditClick}
                variant="secondary"
                className="flex-1 h-14 text-base font-medium rounded-[0.6rem] flex items-center justify-center gap-2"
              >
                {!isEditingBio && <Pencil className="w-5 h-5" />}
                {isEditingBio ? 'Cancel' : 'Edit'}
              </Button>
              <Button 
                onClick={isEditingBio ? handleSaveBio : () => setUploadSheetOpen(true)}
                className="flex-1 h-14 text-base font-medium rounded-[0.6rem] flex items-center justify-center gap-2"
              >
                {!isEditingBio && <Sparkles className="w-5 h-5" />}
                {isEditingBio ? 'Save' : 'Add Designs'}
              </Button>
            </div>
          )}

          {/* Follow and Message Buttons - show if not viewing own page and not an admin */}
          {!isOwnPage && !isAdminModeActive && (
            <div className="mb-0 flex gap-3 w-full">
              <Button 
                onClick={handleFollowClick}
                variant="secondary"
                className={`flex-1 h-14 text-base font-medium rounded-[0.6rem] ${
                  isFollowing ? 'flex items-center justify-center gap-2' : 'flex items-center justify-center'
                }`}
                disabled={isFollowLoading}
              >
                {isFollowLoading ? 'Loading...' : isFollowing ? (
                  <>
                    <Check className="w-4 h-4" />
                    Following
                  </>
                ) : 'Follow'}
              </Button>
              <Button 
                onClick={handleContactArtist}
                className="flex-1 h-14 text-base font-medium rounded-[0.6rem] flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Message
              </Button>
            </div>
          )}


          {/* Tab Navigation - only show tabs if artist has multiple collections with items */}
          {(hasFlashes || hasOnskin || hasHealed) && (
            <div className="my-4 flex items-center justify-center">
              <div className="flex gap-8 items-center">
                {hasFlashes && (
                  <button 
                    onClick={() => handleTabChange('flash')}
                    className={`text-base font-medium pb-2 transition-colors ${
                      activeTab === 'flash' 
                        ? 'text-foreground border-b-2 border-primary' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('navigation.flashes')}
                  </button>
                )}
                {hasOnskin && (
                  <button 
                    onClick={() => handleTabChange('onskin')}
                    className={`text-base font-medium pb-2 transition-colors ${
                      activeTab === 'onskin' 
                        ? 'text-foreground border-b-2 border-primary' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('navigation.onskin')}
                  </button>
                )}
                {hasHealed && (
                  <button 
                    onClick={() => handleTabChange('healed')}
                    className={`text-base font-medium pb-2 transition-colors ${
                      activeTab === 'healed' 
                        ? 'text-foreground border-b-2 border-primary' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('navigation.healed')}
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`text-base font-medium pb-2 transition-colors ${
                    activeTab === 'reviews' 
                      ? 'text-foreground border-b-2 border-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Reviews
                </button>
              </div>
            </div>
          )}
          
          {/* Spacing when no tabs (only one collection) */}
          {(hasFlashes !== null || hasOnskin !== null || hasHealed !== null) && (
            <div className="my-4" />
          )}

          {/* Tab Content - using display toggle instead of conditional rendering to preserve state */}
          <div className="min-h-[200px]">
            <div style={{ display: activeTab === 'onskin' ? 'block' : 'none' }}>
              <Grid 
                refetchNonce={refreshKey}
                uploadRevision={uploadGridRevision}
                selectedCollections={['portfolioItems']}
                selectedStyle={t('onskin.filters.allStyles')}
                selectedArtist={artistId || ""}
                selectedLocations={[t('onskin.filters.allLocations')]}
                sorting="recent"
                editMode={isEditingPortfolio}
                onDelete={handleDeletePortfolioItem}
                excludeItemIds={excludePortfolioIdsAfterDelete}
                onFeedItemIdsChange={onPortfolioFeedIdsChange}
                isProfilePage={true}
                onDetailClick={handleOpenDetail}
              />
            </div>
            <div style={{ display: activeTab === 'flash' ? 'block' : 'none' }}>
              <Grid 
                refetchNonce={refreshKey}
                uploadRevision={uploadGridRevision}
                selectedCollections={['flashes']}
                selectedStyle={t('onskin.filters.allStyles')}
                selectedArtist={artistId}
                selectedLocations={[t('onskin.filters.allLocations')]}
                sorting="recent"
                editMode={isEditingPortfolio}
                onDelete={handleDeleteFlash}
                excludeItemIds={excludeFlashIdsAfterDelete}
                onFeedItemIdsChange={onFlashFeedIdsChange}
                onMetadataChange={handleFlashMetadataChange}
                onMetadataSave={handleFlashMetadataSave}
                onRefreshFlashes={handleRefreshFlashes}
                isProfilePage={true}
                onDetailClick={handleOpenDetail}
              />
            </div>
            <div style={{ display: activeTab === 'healed' ? 'block' : 'none' }}>
              <Grid 
                refetchNonce={refreshKey}
                uploadRevision={uploadGridRevision}
                selectedCollections={['healedPortfolioItems']}
                selectedStyle={t('onskin.filters.allStyles')}
                selectedArtist={artistId || ""}
                selectedLocations={[t('onskin.filters.allLocations')]}
                sorting="recent"
                editMode={isEditingPortfolio}
                onDelete={handleDeleteHealedPortfolioItem}
                excludeItemIds={excludeHealedIdsAfterDelete}
                onFeedItemIdsChange={onHealedFeedIdsChange}
                isProfilePage={true}
                onDetailClick={handleOpenDetail}
              />
            </div>
            <div style={{ display: activeTab === 'reviews' ? 'block' : 'none' }}>
              <div className="flex flex-col gap-4">
                {/* Reviews Header with Stats */}
                <div className="bg-card border border-border rounded-[0.6rem] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-2">
                      <h3 className="text-lg font-semibold text-foreground">
                        Client Reviews
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        All reviews are verified by our team or from real bookings on our platform.
                      </p>
                    </div>
                    <Button 
                      onClick={handleWriteReviewClick}
                      aria-label="Write a review"
                      className="h-12 w-12 p-0 rounded-md flex items-center justify-center flex-shrink-0"
                    >
                      <SquarePen className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const avgRating = reviews.length > 0 
                            ? reviews.reduce((sum, r) => sum + r.starRating, 0) / reviews.length
                            : 0
                          return (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= avgRating
                                  ? "fill-[#FFA500] text-[#FFA500]"
                                  : "text-muted-foreground"
                              }`}
                            />
                          )
                        })}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        ({reviews.length})
                      </span>
                    </div>

                    <Sheet open={isReviewSortOpen} onOpenChange={setIsReviewSortOpen}>
                      <SheetTrigger asChild>
                        <Button 
                          variant="secondary"
                          className="rounded-lg whitespace-nowrap min-w-fit flex items-center gap-2 text-filter-pill !font-semibold"
                        >
                          <ArrowUpDown className="w-4 h-4" />
                          <span className="text-sm">{selectedReviewSortLabel}</span>
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="bottom" className="h-auto max-h-[400px] flex flex-col p-6" hideCloseButton>
                        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                          <SheetTitle className="text-xl font-semibold">Sort Reviews</SheetTitle>
                        </SheetHeader>
                        
                        <div className="flex-1 overflow-y-auto">
                          <div className="space-y-0">
                            {reviewSortOptions.map((option) => (
                              <div key={option.value} className="flex items-center py-4 border-b border-border/30 last:border-b-0">
                                <Button
                                  variant="ghost"
                                  className="w-full justify-start h-auto p-0 hover:bg-transparent"
                                  onClick={() => {
                                    setReviewSortOrder(option.value)
                                    setIsReviewSortOpen(false)
                                  }}
                                >
                                  <div className="flex items-center gap-3 w-full">
                                    <div className="flex items-center gap-2 flex-1">
                                      <span className="text-base font-medium text-left">
                                        {option.label}
                                      </span>
                                      {reviewSortOrder === option.value && (
                                        <div className="w-2 h-2 rounded-full bg-primary ml-auto" />
                                      )}
                                    </div>
                                  </div>
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button
                            className="flex-1"
                            onClick={() => setIsReviewSortOpen(false)}
                          >
                            Close
                          </Button>
                        </div>
                      </SheetContent>
                    </Sheet>
                  </div>
                </div>

                {/* Reviews List */}
                {loadingReviews ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-muted-foreground">Loading reviews...</div>
                  </div>
                ) : reviews.length === 0 ? (
                  null
                ) : (
                  <div className="flex flex-col gap-3 pb-8">
                    {getSortedReviews().map((review) => (
                      <ReviewCard
                        key={review.id}
                        rating={review.starRating}
                        text={review.reviewText}
                        username={review.isAnonymous ? "Anonymous" : review.username || "User"}
                        avatarUrl={review.isAnonymous ? undefined : review.userAvatarUrl}
                        date={review.createdAt}
                        isAnonymous={review.isAnonymous}
                        isVerifiedClient={review.isVerifiedClient}
                        reviewUrls={review.reviewUrls}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
      
      {/* Login Modal - shown when Follow is clicked without auth */}
      <AuthModal 
        isOpen={showLoginModal}
        onOpenChange={setShowLoginModal}
        dismissible={true}
        defaultMode="signup"
        source="artist_page_follow"
        onSuccess={handleLoginSuccess}
      />

      <AuthModal
        isOpen={showReviewAuthModal}
        onOpenChange={setShowReviewAuthModal}
        dismissible={true}
        defaultMode="signup"
        source="artist_page_write_review"
        onSuccess={handleReviewAuthSuccess}
      />

      {/* Upload Sheet */}
      <UploadSheet
        isOpen={uploadSheetOpen}
        onOpenChange={(open) => {
          setUploadSheetOpen(open)
        }}
        type={uploadType}
        onUpload={handleUpload}
        onTypeChange={setUploadType}
        artistId={artistId || ''}
        isAdmin={isAdminModeActive}
      />

      {/* Banner Cropper Modal */}
      {showBannerCropper && bannerImageFile && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6 backdrop-blur-sm">
          <BannerImageCropper
            imageFile={bannerImageFile}
            onCropComplete={handleBannerCropComplete}
            onCancel={handleBannerCropCancel}
          />
        </div>
      )}

      {/* Review Write Modal */}
      <FullScreenDialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <FullScreenDialogContent>
          <ReviewWriteContent 
            artistId={artistId || ''}
            artistName={artist?.displayName || undefined}
            onBack={() => {
              setShowReviewModal(false)
              // Refresh reviews list when user exits (after viewing success screen)
              setRefreshReviewsKey(prev => prev + 1)
            }}
          />
        </FullScreenDialogContent>
      </FullScreenDialog>

      {/* Detail Modal for artist page */}
      <FullScreenDialog open={detailModalOpen} onOpenChange={handleCloseDetailModal}>
        <FullScreenDialogContent>
          {detailType === 'flash' && detailId && (
            <FlashDetailContent 
              flashId={detailId}
              onBack={handleCloseDetailModal}
              shareUrl={`${window.location.origin}/flashes/${detailId}`}
            />
          )}
          {detailType === 'tattoo' && detailId && (
            <TattooDetailContent 
              tattooId={detailId}
              onBack={handleCloseDetailModal}
              shareUrl={`${window.location.origin}/tattoos/${detailId}`}
            />
          )}
          {detailType === 'healed' && detailId && (
            <HealedDetailContent 
              healedId={detailId}
              onBack={handleCloseDetailModal}
              shareUrl={`${window.location.origin}/healed/${detailId}`}
            />
          )}
        </FullScreenDialogContent>
      </FullScreenDialog>
    </>
  )
} 
